/**
 * TrajectoryTranscript — flat, progressively-disclosed transcript rows.
 *
 * One collapsible row per event with a role-colored left border, a role
 * label column, and a one-line peek — so the collapsed conversation scans
 * as a story at a glance. Everything starts collapsed; expand what you're
 * reading. Tool calls show `name(args…) → result…` in the peek and the
 * full payloads when expanded. The final assistant reply carries an
 * accent ring.
 *
 * Shared with the transcript panel behind /conversations; /review adds
 * the amber flag pin anchored to the flagged trace's first event.
 */

import { useEffect, useMemo, useState } from 'react'

import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Markdown } from './Markdown'
import { requestAnchorId } from './requestCitations'
import {
  flagAnchorIndex,
  jsonDisplaySegments,
  type JsonSegmentKind,
  type ToolEvent,
  type TranscriptEvent,
  type TranscriptTurn,
} from './transcript'

/** One-line preview for collapsed rows. */
function preview(text: string, max = 160): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length <= max ? flat : `${flat.slice(0, max)}…`
}

/** How each event kind labels and colors its row. */
const ROW_STYLE: Record<
  TranscriptEvent['kind'],
  { border: string; label: string; labelClass: string }
> = {
  system: {
    border: 'border-l-muted-foreground',
    label: 'system',
    labelClass: 'text-muted-foreground',
  },
  user: { border: 'border-l-warning', label: 'user', labelClass: 'text-warning' },
  assistant: { border: 'border-l-info', label: 'assistant', labelClass: 'text-info' },
  thinking: { border: 'border-l-info', label: 'thinking', labelClass: 'text-info' },
  tool: { border: 'border-l-secondary', label: 'tool', labelClass: 'text-secondary' },
  raw: {
    border: 'border-l-destructive',
    label: 'raw',
    labelClass: 'text-destructive',
  },
}

function eventPeek(event: TranscriptEvent): string {
  if (event.kind === 'tool') {
    const call = `${event.name}(${preview(event.args, 80)})`
    return event.result === null
      ? `${call} → (no output captured)`
      : `${call} → ${preview(event.result, 80)}`
  }
  if (event.kind === 'raw') return preview(event.text)
  if (event.kind === 'user' && event.images.length > 0) {
    const n = event.images.length
    const count = `${String(n)} ${n === 1 ? 'image' : 'images'}`
    return event.text.trim() === '' ? count : `${count} + ${preview(event.text)}`
  }
  return event.text.trim() === '' ? '(empty)' : preview(event.text)
}

/**
 * One image the model was sent. Thumbnail in the row; click opens it at
 * full size in a dialog. Data URIs cannot open in a new tab, so the
 * dialog is the only way to see the full image.
 *
 * Only inline data URIs render as <img>. Trace bodies are customer
 * content, so a remote URL would make the reviewer's browser fetch
 * whatever the client put there (a tracking pixel, an internal host).
 * Remote references show as text instead.
 */
export function ImageThumb({ url, index }: { url: string; index: number }): React.JSX.Element {
  const ordinal = String(index + 1)
  const alt = `Image ${ordinal} sent to the model`
  if (!/^data:image\//i.test(url)) {
    return (
      <span className="break-all font-mono text-xs text-muted-foreground">
        Image {ordinal} (remote, not loaded): {url}
      </span>
    )
  }
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={`Open image ${ordinal} at full size`}
          className="border border-border bg-muted/30 p-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <img src={url} alt={alt} className="max-h-24 max-w-[12rem] object-contain" />
        </button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[90vh] w-auto max-w-[90vw] items-center justify-center overflow-auto p-4 pt-10">
        <DialogTitle className="sr-only">{alt}</DialogTitle>
        <img src={url} alt={alt} className="max-h-[85vh] max-w-full" />
      </DialogContent>
    </Dialog>
  )
}

/**
 * Tool payload with JSON syntax coloring (Data role — mono, semantic
 * tokens: keys info, strings ink, numbers/literals warning). The
 * segments come from lexing the ORIGINAL text — never a parse/re-stringify
 * round trip, which would corrupt >53-bit numbers and drop duplicate keys.
 * Payloads that aren't JSON (plain-text tool output) render uncolored as-is.
 */
// String VALUES stay ink — payloads are often one huge string, and tinting
// it made the block a wall of color. Keys carry the structure, so they get
// the hue; numbers/literals whisper warning.
const SEGMENT_CLASS: Record<Exclude<JsonSegmentKind, 'plain'>, string> = {
  key: 'text-info',
  string: 'text-foreground',
  number: 'text-warning',
  literal: 'text-warning',
}

function ToolPayload({ text }: { text: string }): React.JSX.Element {
  const base = 'whitespace-pre-wrap break-words font-mono text-xs leading-relaxed'
  // Memoized: lexing a multi-hundred-KB payload on every keystroke of the
  // transcript search made filtering visibly stall.
  const segments = useMemo(() => jsonDisplaySegments(text), [text])
  if (segments === null) {
    return <pre className={`${base} text-foreground`}>{text}</pre>
  }
  return (
    <pre className={`${base} text-muted-foreground`}>
      {segments.map((seg, i) =>
        seg.kind === 'plain' ? (
          seg.text
        ) : (
          <span key={i} className={SEGMENT_CLASS[seg.kind]}>
            {seg.text}
          </span>
        ),
      )}
    </pre>
  )
}

/**
 * One collapsible input/output section: labeled header strip over a white
 * payload. Input starts open (it's usually short and says what the call
 * did); output starts collapsed — it can be huge, and the row's peek
 * already summarizes it.
 */
function ToolSection({
  label,
  peek,
  open = false,
  children,
}: {
  label: React.ReactNode
  /** One-line truncated payload preview, like the outer row's summary. */
  peek: string
  open?: boolean
  children: React.ReactNode
}): React.JSX.Element {
  // Payloads mount only once the section has been opened: <details> children
  // are always mounted by React, so a collapsed multi-hundred-KB payload
  // would otherwise pay its full lex + span-per-token DOM cost up front.
  // Stays mounted after the first open, preserving the payload memo.
  const [everOpened, setEverOpened] = useState(open)
  return (
    <details
      className="group/section border border-border bg-card"
      open={open}
      onToggle={(e) => {
        if (e.currentTarget.open) setEverOpened(true)
      }}
    >
      <summary className="flex cursor-pointer select-none items-baseline gap-2 bg-muted px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:bg-muted/70 group-open/section:border-b group-open/section:border-border [&::-webkit-details-marker]:hidden">
        <span className="inline-block flex-none transition-transform group-open/section:rotate-90">
          ▸
        </span>
        <span className="flex-none">{label}</span>
        <span className="min-w-0 flex-1 truncate font-sans text-xs normal-case tracking-normal">
          {peek}
        </span>
      </summary>
      {everOpened && <div className="px-3 py-2">{children}</div>}
    </details>
  )
}

function ToolRowBody({ event }: { event: ToolEvent }): React.JSX.Element {
  return (
    <div className="mx-3 mb-2.5 flex flex-col gap-1.5">
      <ToolSection
        open
        peek={preview(event.args, 120)}
        label={
          <>
            input{' '}
            <span className="normal-case tracking-normal text-secondary">{event.name}</span>
          </>
        }
      >
        <ToolPayload text={event.args} />
      </ToolSection>
      <ToolSection
        label="output"
        peek={event.result === null ? '(no output captured)' : preview(event.result, 120)}
      >
        {event.result === null ? (
          <div className="font-mono text-xs italic text-muted-foreground">
            (no output captured)
          </div>
        ) : (
          <ToolPayload text={event.result} />
        )}
      </ToolSection>
    </div>
  )
}

function RowBody({ event }: { event: TranscriptEvent }): React.JSX.Element {
  if (event.kind === 'tool') {
    return <ToolRowBody event={event} />
  }
  // Assistant text is the model's own output, so its markdown renders.
  // Everything else stays verbatim: user/system rows carry harness
  // scaffolding (<SYSTEM_CONTEXT> blocks etc.) that markdown would mangle.
  if (event.kind === 'assistant') {
    return (
      <Markdown
        text={event.text}
        className="px-3 pb-2.5 pt-0.5 text-sm leading-relaxed text-foreground"
      />
    )
  }
  const isThinking = event.kind === 'thinking'
  const images = event.kind === 'user' ? event.images : []
  // Message text is prose (Body role) — sans, not mono. Mono is reserved for
  // machine/data text like the tool-call blocks above.
  return (
    <>
      {event.text !== '' && (
        <pre
          className={`px-3 pb-2.5 pt-0.5 font-sans text-sm leading-relaxed ${
            isThinking ? 'italic text-muted-foreground' : 'text-foreground'
          } whitespace-pre-wrap break-words`}
        >
          {event.text}
        </pre>
      )}
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 pb-2.5 pt-0.5">
          {images.map((url, i) => (
            <ImageThumb key={i} url={url} index={i} />
          ))}
        </div>
      )}
    </>
  )
}

/** Per-request numbers a row can carry now that there is no turns table. */
export interface TurnMeta {
  latencyMs: number
  statusCode: number
}

interface TranscriptRowProps {
  event: TranscriptEvent
  open: boolean
  /** Accent ring on the conversation's final assistant reply. */
  highlight: boolean
  /** Right-side latency, shown on the last row of each turn. */
  latencyMs: number | null
  /** The request that produced this row failed — the border says so. */
  failed: boolean
  /**
   * For a tool row whose output arrived in a LATER request: that request's
   * number. The result renders here, on the call, but the ask panel cites
   * the request that carried it (TRA-1080), so the row says where the
   * citation points. Null when the result is on this row's own request,
   * missing, or the renderer has no request numbering.
   */
  resultRequest: number | null
}

function TranscriptRow({
  event,
  open,
  highlight,
  latencyMs,
  failed,
  resultRequest,
}: TranscriptRowProps): React.JSX.Element {
  const style = ROW_STYLE[event.kind]
  const label = event.kind === 'raw' ? event.label : style.label
  return (
    <details
      className={`border border-l-2 bg-card ${
        failed ? 'border-destructive' : `border-border ${style.border}`
      } ${highlight ? 'ring-1 ring-info' : ''}`}
      open={open}
    >
      <summary className="flex cursor-pointer select-none items-baseline gap-3 px-3 py-2 hover:bg-muted/50 [&::-webkit-details-marker]:hidden">
        <span
          className={`w-[86px] flex-none truncate font-mono text-[11px] uppercase tracking-wider ${style.labelClass}`}
        >
          {label}
        </span>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {eventPeek(event)}
        </span>
        {resultRequest !== null && (
          <span className="flex-none font-mono text-[11px] tabular-nums text-muted-foreground">
            result in request {resultRequest}
          </span>
        )}
        {latencyMs !== null && (
          <span className="flex-none font-mono text-[11px] tabular-nums text-muted-foreground">
            {(latencyMs / 1000).toFixed(1)}s
          </span>
        )}
      </summary>
      <RowBody event={event} />
    </details>
  )
}

/**
 * Whether rows start open. Collapsed is the default everywhere — the
 * peek list IS the at-a-glance view; expand what you're reading.
 */
export type DisclosureMode = 'collapsed' | 'expanded'

/** Index of the last assistant event — the conversation's final reply. */
function finalReplyIndex(events: TranscriptEvent[]): number | null {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    if (events[i]?.kind === 'assistant') return i
  }
  return null
}

interface TranscriptEventsProps {
  events: TranscriptEvent[]
  /** Disclosure state; bump `version` to re-apply it after user toggles. */
  mode?: DisclosureMode
  version?: number
  /** Per-trace request numbers; keyed by traceId. Absent = no chrome. */
  meta?: Map<string, TurnMeta>
  /**
   * The UNFILTERED event list, when `events` is a filtered view of it.
   * Row keys come from each event's position here, so a search or tool
   * filter shrinking the list can't hand an open row's state to whatever
   * different event now occupies its slot.
   */
  allEvents?: TranscriptEvent[]
  unfilteredEvents?: TranscriptEvent[]
  reviewIndex?: number | null
  renderReview?: () => React.ReactNode
}

/**
 * Index of each trace's LAST event among the given (possibly filtered)
 * events — where that turn's latency renders.
 */
function lastEventPerTrace(events: TranscriptEvent[]): Map<string, number> {
  const last = new Map<string, number>()
  events.forEach((event, i) => last.set(event.traceId, i))
  return last
}

/** The flat transcript with no review chrome, used by TranscriptPanel. */
export function TranscriptEvents({
  events,
  mode = 'collapsed',
  version = 0,
  meta,
  allEvents,
  unfilteredEvents,
  renderReview,
  reviewIndex,
}: TranscriptEventsProps): React.JSX.Element {
  // Row chrome (final-reply ring, per-turn latency placement) is a fact
  // about the CONVERSATION, so it's computed from the full list — deriving
  // it from a filtered view moved the accent and latency onto whichever
  // rows happened to survive the filter.
  const full = allEvents ?? events
  const finalIndex = finalReplyIndex(full)
  const lastOfTrace = lastEventPerTrace(full)
  const rows = unfilteredEvents ?? events
  // Filtered views share object identity with the full list, so indexOf
  // gives each event a filter-stable ordinal.
  const stableIndex = (event: TranscriptEvent, i: number): number =>
    allEvents === undefined ? i : allEvents.indexOf(event)
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((event, i) => {
        const turn = meta?.get(event.traceId)
        const at = stableIndex(event, i)
        return (
          // `<details>` open state is uncontrolled after the first toggle, so
          // the version in the key remounts rows when collapse/expand-all is
          // pressed — that reset is the feature.
          <div key={at} hidden={!events.includes(event)} className="group/trace-row relative">
          <TranscriptRow
            key={version}
            event={event}
            open={mode === 'expanded'}
            highlight={at === finalIndex}
            latencyMs={
              turn !== undefined && lastOfTrace.get(event.traceId) === at
                ? turn.latencyMs
                : null
            }
            // Scoped to the trace's LAST row: a failed request still resent
            // the whole history, and painting every message it carried red
            // marked the entire transcript for one error.
            failed={
              turn !== undefined &&
              turn.statusCode >= 400 &&
              lastOfTrace.get(event.traceId) === at
            }
            // No request numbering outside /review, so nothing to point at.
            resultRequest={null}
          />
          {renderReview !== undefined && i === reviewIndex && (
            <div className="absolute -left-8 top-1">
              {renderReview()}
            </div>
          )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * A flag pin. A real button so it's keyboard-focusable; the reason popover
 * opens on hover or focus (group-focus-within) and is linked via
 * aria-describedby. The open flag is amber; the run's other flags
 * (TRA-1088) sit on their own requests in the quiet tone, so the eye still
 * finds the one being reviewed first. Ids are per trace: a run can carry
 * several pins at once.
 */
function FlagPin({
  traceId,
  source,
  reason,
  current,
}: {
  traceId: string
  source: string
  reason: string
  current: boolean
}): React.JSX.Element {
  const popoverId = `review-flag-popover-${traceId}`
  return (
    // Each pin is its own stacking context, so the popover can only rise
    // above a later pin if its pin does. Lift the open one.
    <span className="group absolute -left-[31px] top-1 z-10 inline-flex hover:z-20 focus-within:z-20">
      <button
        type="button"
        aria-describedby={popoverId}
        aria-label={current ? source : `${source} (another flag on this run)`}
        className={`flex h-6 w-6 items-center justify-center ring-4 ring-background ${
          current ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
        }`}
      >
        ⚑
      </button>
      <span
        id={popoverId}
        role="tooltip"
        className="invisible absolute left-0 top-[calc(100%+6px)] w-72 border border-border bg-card px-3 py-2.5 text-xs leading-relaxed text-foreground opacity-0 motion-safe:transition-opacity group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
      >
        <span
          className={`mb-0.5 block font-mono text-xs uppercase tracking-wider ${
            current ? 'text-warning' : 'text-muted-foreground'
          }`}
        >
          {source}
        </span>
        {reason || 'No reason recorded.'}
      </span>
    </span>
  )
}

/**
 * The request number a tool row's output arrived with, when that is not
 * the row's own request. Null when the output came with the call's own
 * request, or never came, so those rows stay quiet.
 */
function resultRequestOf(
  event: TranscriptEvent,
  requestOf: Map<string, number>,
): number | null {
  if (event.kind !== 'tool') return null
  if (event.resultTraceId === null || event.resultTraceId === event.traceId) return null
  return requestOf.get(event.resultTraceId) ?? null
}

/** One flag to pin on its request. `current` marks the flag being reviewed. */
export interface TranscriptFlag {
  traceId: string
  source: string
  reason: string
  current: boolean
}

interface TrajectoryTranscriptProps {
  /** Per-request sections (`buildTurns`), oldest first. */
  turns: TranscriptTurn[]
  /** Every flag on the run (TRA-1088); at most one is `current`. */
  flags: TranscriptFlag[]
  /**
   * Request to scroll to; `nonce` bumps on every click so re-clicking the
   * same citation scrolls again. Null = nothing selected.
   */
  activeRequest: { n: number; nonce: number } | null
}

/** The /review transcript: continuous rows with citation anchors and flag pins. */
export function TrajectoryTranscript({
  turns,
  flags,
  activeRequest,
}: TrajectoryTranscriptProps): React.JSX.Element {
  // Keep request identity for citations without visual request sections.
  const events = useMemo(() => turns.flatMap((turn) => turn.events), [turns])
  // Request number per trace, for the tool rows whose output arrived in a
  // later request than the call (see `resultRequest`).
  const requestOf = useMemo(
    () => new Map(turns.map((turn) => [turn.traceId, turn.index])),
    [turns],
  )
  // Flat event index → the flag pinned there. One pin per trace at most
  // (a labels row is keyed by trace), so two flags never share an index.
  const pinAt = useMemo(() => {
    const out = new Map<number, TranscriptFlag>()
    for (const flag of flags) {
      const at = flagAnchorIndex(events, flag.traceId)
      if (at !== null) out.set(at, flag)
    }
    return out
  }, [events, flags])
  const finalIndex = finalReplyIndex(events)

  useEffect(() => {
    if (activeRequest === null) return
    const anchor = document.getElementById(requestAnchorId(activeRequest.n))
    // Null only if the citation outran the page (the chat's cached
    // trajectory can be 15 minutes older than the transcript, or newer).
    // Nothing to scroll to when that request is absent.
    anchor?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [activeRequest])

  // Flat index of each section's first event, so rows can be keyed and
  // matched against the flag/final indexes without a counter in render.
  const starts = useMemo(() => {
    const out: number[] = []
    let at = 0
    for (const turn of turns) {
      out.push(at)
      at += turn.events.length
    }
    return out
  }, [turns])

  return (
    <div className="mx-auto flex w-full max-w-[840px] flex-col gap-1.5 px-8 py-6 pl-12">
      {turns.map((turn, t) => (
        <div
          key={turn.traceId}
          id={requestAnchorId(turn.index)}
          data-request={turn.index}
          className="flex scroll-mt-4 flex-col gap-1.5"
        >
          {turn.events.length === 0 && (
            // A request whose delta was only tool results (they attach to
            // earlier rows) and whose reply was empty adds no row; say so,
            // so citations still have a visible destination.
            <p className={`flex items-baseline gap-3 px-3 text-xs ${
              turn.statusCode >= 400 ? 'text-destructive' : 'text-muted-foreground'
            } ${activeRequest?.n === turn.index ? 'ring-1 ring-info' : ''}`}>
              <span>No new messages in this request.</span>
              {turn.statusCode >= 400 && <span>HTTP {turn.statusCode}</span>}
              <span className="ml-auto font-mono">{(turn.latencyMs / 1000).toFixed(1)}s</span>
            </p>
          )}
          {turn.events.map((event, j) => {
            const i = (starts[t] ?? 0) + j
            const row = (
              <TranscriptRow
                key={i}
                event={event}
                open={false}
                highlight={i === finalIndex || (j === 0 && activeRequest?.n === turn.index)}
                latencyMs={j === turn.events.length - 1 ? turn.latencyMs : null}
                failed={j === turn.events.length - 1 && turn.statusCode >= 400}
                resultRequest={resultRequestOf(event, requestOf)}
              />
            )
            const pin = pinAt.get(i)
            if (pin !== undefined) {
              return (
                // The current flag's id is the scroll target for the
                // header's "Go to flag" button — one per page, so a bare
                // id is fine. Other flags get no id: the button follows
                // the flag being reviewed.
                <div
                  key={i}
                  id={pin.current ? 'review-flag-anchor' : undefined}
                  className="relative"
                >
                  <FlagPin
                    traceId={pin.traceId}
                    source={pin.source}
                    reason={pin.reason}
                    current={pin.current}
                  />
                  {row}
                </div>
              )
            }
            return row
          })}
        </div>
      ))}
    </div>
  )
}
